import { TextNoteElement } from '../reading-utils/page-elements/text-note-element';
import { AnnotationTypes } from './enums';
import { PageElement } from '../reading-utils/page-elements';
import { Rectangle } from '../interfaces';
import { isRectIntersectRect, isAnnotationType } from '../common';
import { PageOptions } from './interfaces';
import { Core } from '@pdftron/webviewer';


export class PageTextNotesManager {
    pageOptions: PageOptions | null = null;

    constructor(private readonly annotationManager: Core.AnnotationManager) { }

    setPageOptions(opts: PageOptions): void {
        this.pageOptions = opts;
    }

    getTextNotes(pageNumber: number, type: AnnotationTypes = AnnotationTypes.FreeText): Core.Annotations.Annotation[] {
        let result = this.annotationManager.getAnnotationsList();

        if (pageNumber) {
            result = result.filter(x => x.getPageNumber() === pageNumber);
        }

        if (type) {
            result = result.filter(x => isAnnotationType(x, [type], true));
        }

        return result;
    }

    addTextNote(pageWords: PageElement[], textNote: Core.Annotations.Annotation): TextNoteElement[] {
        const textContent = textNote.getContents();
        const words = textContent.split(' ');

        const textNoteWords: TextNoteElement[] = [];


        words.forEach(word => {
            if (this.pageOptions) {
                const annotationElement: TextNoteElement = new TextNoteElement(
                    textNote, word, this.pageOptions.xOffset, this.pageOptions.height
                );

                textNoteWords.push(annotationElement);
            }
        });

        this.putTextNotesToReadingOrder(pageWords, textNoteWords);

        return textNoteWords;
    }

    deleteTextNote(pageWords: PageElement[], textNoteId: string): PageElement[][] | null {
        let deletedTextNoteElementId = 0;
        const deletedWords: PageElement[][] = [];
        while (deletedTextNoteElementId !== -1) {
            deletedTextNoteElementId = pageWords.findIndex(x => x instanceof TextNoteElement &&
                (x.element as Core.Annotations.Annotation).Id === textNoteId);

            if (deletedTextNoteElementId !== -1) {
                deletedWords.push(pageWords.splice(deletedTextNoteElementId, 1));
            }
        }

        return deletedWords;
    }

    private isWordsFromSameTextNote(textNotes: TextNoteElement[]): boolean {
        return textNotes.every(x => (x.element as Core.Annotations.Annotation).Id === (textNotes[0].element as Core.Annotations.Annotation).Id);
    }

    private putTextNotesToReadingOrder(pageWords: PageElement[], textNotes: TextNoteElement[]): void {
        if (this.isWordsFromSameTextNote(textNotes)) {
            const idx = this.getClosestWordIdxForTextNote(pageWords, textNotes[0]);
            pageWords.splice(idx + 1, 0, ...textNotes);
            return;
        }

        textNotes.forEach((textNote: TextNoteElement) => {
            const idx = this.getClosestWordIdxForTextNote(pageWords, textNote);
            pageWords.splice(idx + 1, 0, textNote);
        });
    }

    private getClosestWordIdxForTextNote(pageWords: PageElement[], textNote: TextNoteElement): number {
        let closestWordIdx = -1;
        let sameLineWords: { word: PageElement, index: number }[] = [];
        let yLevel = -1;

        pageWords.forEach((word: PageElement, index: number) => {
            if (word.boundary && textNote.boundary) {
                const rect: Rectangle = {
                    x: textNote.boundary.x,
                    y: word.boundary.y,
                    width: textNote.boundary.width,
                    height: word.boundary.height
                };

                if (isRectIntersectRect(textNote.boundary, rect) && textNote.boundary.x > word.boundary.x + word.boundary.width) {
                    if (Math.abs(word.boundary.y - textNote.boundary.y) < yLevel || yLevel === -1) {
                        yLevel = word.boundary.y;
                        sameLineWords = [];
                    }

                    sameLineWords.push({ word, index });
                }
            }
        });

        if (sameLineWords.length > 0) {
            const distances: number[] = [];

            sameLineWords.forEach(item => {
                if (item.word.boundary && textNote.boundary) {
                    distances.push(textNote.boundary.x - (item.word.boundary.x + item.word.boundary.width));
                }
            });

            closestWordIdx = sameLineWords[distances.indexOf(Math.min(...distances))].index;
        } else {
            for (let i = pageWords.length - 1; i >= 0; i--) {
                const pageWord = pageWords[i];
                if (pageWord && pageWord.boundary && textNote.boundary &&
                    pageWord.boundary.y > textNote.boundary.y + textNote.boundary.height) {

                    closestWordIdx = i;
                    break;
                }
            }
        }

        return closestWordIdx;
    }
}
